import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { SearchInput } from "@/pages/projekte/SearchInput";
import type { SupportTicket } from "@/lib/support";
import { USER_FILTER_CHIPS } from "@/pages/support/constants";
import { UserDetailPanel } from "@/pages/support/UserDetailPanel";
import { UserTicketList } from "@/pages/support/UserTicketList";
import { applyUserFilters, computeUserStats, sortUserTickets, type UserFilters } from "@/pages/support/utils";

const DEFAULT_FILTERS: UserFilters = {
  search: "",
  statusFilter: "active",
};

export function UserView({
  tickets,
  loading,
  onNewTicket,
  onRefresh,
}: {
  tickets: SupportTicket[];
  loading: boolean;
  onNewTicket: () => void;
  onRefresh: () => void;
}) {
  const [filters, setFilters] = useState<UserFilters>(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const stats = useMemo(() => computeUserStats(tickets), [tickets]);

  const filtered = useMemo(
    () => sortUserTickets(applyUserFilters(tickets, filters)),
    [tickets, filters],
  );

  const selected = filtered.find((t) => t.id === selectedId) ?? tickets.find((t) => t.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedId != null && !tickets.some((t) => t.id === selectedId)) {
      setSelectedId(null);
      setMobileShowDetail(false);
    }
  }, [tickets, selectedId]);

  useEffect(() => {
    const first = filtered[0];
    if (first && selectedId == null && filters.statusFilter === "active") {
      setSelectedId(first.id);
    }
  }, [filtered, selectedId, filters.statusFilter]);

  function selectTicket(t: SupportTicket) {
    setSelectedId(t.id);
    setMobileShowDetail(true);
  }

  const statCards = [
    { key: "active", label: "Aktiv", value: stats.active, hint: "Offen & in Bearbeitung" },
    { key: "waiting", label: "Rückfragen", value: stats.waiting, hint: "Antwort erforderlich" },
    { key: "resolved", label: "Gelöst", value: stats.resolved, hint: "Bestätigung möglich" },
    { key: "all", label: "Gesamt", value: stats.all, hint: "Alle Tickets" },
  ] as const;

  return (
    <div className="support-user space-y-5">
      <header className="support-user-hero rounded-ios-lg border border-separator bg-gradient-to-br from-card via-card to-accent-soft/20 px-5 py-6 shadow-ios">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-accent">Ernsthaus Support</p>
            <h1 className="mt-1 text-2xl font-semibold text-label">Mein Support-Center</h1>
            <p className="mt-2 text-sm leading-relaxed text-label-secondary">
              Erstelle Tickets, verfolge den Bearbeitungsstand und kommuniziere direkt mit mir – alles an einem Ort.
            </p>
          </div>
          <Button type="button" className="shrink-0 self-start" onClick={onNewTicket}>
            Neues Ticket erstellen
          </Button>
        </div>
      </header>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <button
            key={card.key}
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, statusFilter: card.key }))}
            className={clsx(
              "rounded-ios-lg border px-4 py-3 text-left transition",
              filters.statusFilter === card.key
                ? "border-accent bg-accent-soft/25 ring-1 ring-accent/30"
                : "border-separator bg-card hover:bg-fill-tertiary",
            )}
          >
            <div className="text-xs text-label-secondary">{card.label}</div>
            <div className="text-2xl font-semibold tabular-nums text-label">{card.value}</div>
            <div className="text-[10px] text-label-tertiary">{card.hint}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={filters.search}
          onChange={(search) => setFilters((prev) => ({ ...prev, search }))}
          placeholder="Tickets durchsuchen (Titel, Programm, ID …)"
          className="flex-1"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {USER_FILTER_CHIPS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, statusFilter: chip.key }))}
            className={clsx(
              "rounded-full px-3 py-1.5 text-sm font-medium transition",
              filters.statusFilter === chip.key
                ? "bg-accent text-label-on-accent shadow-sm"
                : "bg-fill-tertiary text-label hover:bg-fill",
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="support-user-inbox grid min-h-[32rem] gap-4 lg:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] lg:items-stretch">
        <aside
          className={clsx(
            "flex min-h-0 flex-col rounded-ios-lg border border-separator bg-card",
            mobileShowDetail ? "hidden lg:flex" : "flex",
          )}
        >
          <div className="shrink-0 border-b border-separator px-3 py-2.5">
            <span className="text-xs text-label-secondary tabular-nums">
              {loading ? "Lädt …" : `${filtered.length} Ticket${filtered.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {loading && tickets.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-label-secondary">
                <Spinner />
              </div>
            ) : filtered.length === 0 ? (
              <EmptyInbox onNewTicket={onNewTicket} hasTickets={tickets.length > 0} />
            ) : (
              <UserTicketList items={filtered} selectedId={selectedId} onSelect={selectTicket} />
            )}
          </div>
        </aside>

        <section className={clsx("min-h-[24rem] lg:min-h-0", !mobileShowDetail && "hidden lg:block")}>
          {selected ? (
            <UserDetailPanel
              ticket={selected}
              onRefresh={onRefresh}
              showBack
              onBack={() => setMobileShowDetail(false)}
            />
          ) : (
            <EmptyDetail onNewTicket={onNewTicket} />
          )}
        </section>
      </div>
    </div>
  );
}

function EmptyInbox({ onNewTicket, hasTickets }: { onNewTicket: () => void; hasTickets: boolean }) {
  return (
    <div className="rounded-ios border border-dashed border-separator px-4 py-8 text-center">
      <div className="text-3xl" aria-hidden>
        {hasTickets ? "🔍" : "📭"}
      </div>
      <p className="mt-2 text-sm font-medium text-label">
        {hasTickets ? "Keine Tickets in dieser Ansicht" : "Noch keine Tickets"}
      </p>
      <p className="mt-1 text-xs text-label-secondary">
        {hasTickets
          ? "Passe die Filter an oder wähle „Alle“."
          : "Erstelle dein erstes Support-Ticket – ich helfe dir gerne weiter."}
      </p>
      {!hasTickets ? (
        <Button type="button" className="mt-4 text-sm" onClick={onNewTicket}>
          Ticket erstellen
        </Button>
      ) : null}
    </div>
  );
}

function EmptyDetail({ onNewTicket }: { onNewTicket: () => void }) {
  return (
    <div className="flex h-full min-h-[20rem] flex-col items-center justify-center rounded-ios-lg border border-dashed border-separator bg-card px-6 text-center">
      <div className="text-4xl" aria-hidden>
        💬
      </div>
      <h3 className="mt-3 text-lg font-semibold text-label">Ticket auswählen</h3>
      <p className="mt-1 max-w-md text-sm text-label-secondary">
        Wähle links ein Ticket, um den Verlauf zu sehen, mir zu antworten oder die Lösung zu bestätigen.
      </p>
      <Button type="button" variant="secondary" className="mt-4 text-sm" onClick={onNewTicket}>
        Neues Ticket
      </Button>
    </div>
  );
}
